/**
 * TreeMiniCard — the compact ancestor card rendered on the family-tree
 * canvas. Shows portrait (double-gold ring, gender-tinted ribbon),
 * name (Latin + optional Malayalam), nickname, life years,
 * birthplace, current place, and generation badge. Admins get a
 * floating pencil button for Quick-Edit.
 *
 * Extracted from FamilyTreeDetail.jsx (previously defined inline as
 * `TreeMiniCard`) so the parent page stays under control.
 */
import { Link } from "react-router-dom";
import { Home, MapPin, Pencil } from "lucide-react";
import AncestorAvatar from "@/components/AncestorAvatar";
import CopyLinkIcon from "@/components/CopyLinkIcon";
import { HERITAGE_PALETTE, TREE_CARD_W, TREE_CARD_H } from "./treeConstants";

export default function TreeMiniCard({ m, treeSlug, deceased, isAdmin, onQuickEdit, onOpen, generation }) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
  const y1 = (m.birth_date || "").slice(0, 4);
  const y2 = (m.death_date || "").slice(0, 4);
  const yrs = y1 && y2 ? `${y1} – ${y2}` : y1 ? `b. ${y1}` : y2 ? `d. ${y2}` : "";
  const gender = (m.gender || "").toLowerCase();
  const ribbon =
    gender === "male"   ? HERITAGE_PALETTE.ribbonMale   :
    gender === "female" ? HERITAGE_PALETTE.ribbonFemale :
                          HERITAGE_PALETTE.ribbonUnknown;
  const birthPlace = m.birth_place || m.origin_location || "";
  const currentLoc = m.current_location || m.location || "";
  const genLabel = (m.generation_override && Number.isFinite(m.generation_override))
    ? `G-${m.generation_override}`
    : (Number.isFinite(generation) ? `G-${generation}` : "");
  return (
    <div className="relative" style={{ width: TREE_CARD_W, height: TREE_CARD_H, flex: "none" }}>
      <Link
        to={`/family/${treeSlug}/member/${m.id}`}
        data-testid={`tree-node-${m.id}`}
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => { if (onOpen) { e.preventDefault(); e.stopPropagation(); onOpen(m); } }}
        className="group flex flex-col items-center text-center rounded-xl bg-white overflow-hidden relative w-full h-full transition-all hover:shadow-lg hover:-translate-y-0.5"
        style={{
          background: deceased ? HERITAGE_PALETTE.deceasedBg : HERITAGE_PALETTE.ivory,
          border: `1px solid ${HERITAGE_PALETTE.border}`,
          boxShadow: "0 1px 3px rgba(160,124,60,0.15), 0 1px 2px rgba(59,46,31,0.06)",
        }}
      >
        {/* Gender ribbon at top */}
        <div
          className="w-full"
          style={{ height: 6, background: ribbon }}
          aria-hidden
        />

        {/* Ornate circular portrait frame — double gold ring */}
        <div
          className="mt-2 rounded-full flex items-center justify-center relative"
          style={{
            width: 74, height: 74,
            padding: 3,
            background: `conic-gradient(from 0deg, ${HERITAGE_PALETTE.ringOuter}, ${HERITAGE_PALETTE.ringInner}, ${HERITAGE_PALETTE.ringOuter})`,
            boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          <div
            className="rounded-full overflow-hidden bg-white flex items-center justify-center"
            style={{
              width: 68, height: 68,
              border: `1px solid ${HERITAGE_PALETTE.ringOuter}`,
            }}
          >
            <AncestorAvatar
              member={m}
              size={66}
              showAddChip={false}
              className="!rounded-full w-full h-full"
            />
          </div>
        </div>

        {/* Nameplate */}
        <div className="px-1.5 pt-1.5 w-full flex-1 flex flex-col items-center justify-start">
          <div
            className="text-[11px] leading-[1.15] font-semibold w-full break-words"
            title={name}
            style={{
              fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
              color: deceased ? HERITAGE_PALETTE.deceasedInk : HERITAGE_PALETTE.ink,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              hyphens: "auto",
            }}
          >
            {name || "—"}
          </div>
          {m.name_ml && (
            <div
              className="text-[10px] leading-tight w-full break-words text-center mt-0.5"
              style={{
                fontFamily: "'Noto Sans Malayalam', 'Manjari', 'Cormorant Garamond', Georgia, serif",
                color: deceased ? HERITAGE_PALETTE.deceasedInk : HERITAGE_PALETTE.copper,
                opacity: 0.9,
                letterSpacing: 0.1,
              }}
              title={`Malayalam: ${m.name_ml}`}
              data-testid={`tree-node-name-ml-${m.id}`}
              lang="ml"
            >
              {m.name_ml}
            </div>
          )}
          {m.nickname && (
            <div
              className="text-[9px] italic leading-tight truncate w-full"
              style={{ color: HERITAGE_PALETTE.copper, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              title={`Nickname: ${m.nickname}`}
              data-testid={`tree-node-nickname-${m.id}`}
            >
              &ldquo;{m.nickname}&rdquo;
            </div>
          )}
          {yrs && (
            <div
              className="text-[10px] italic leading-tight mt-0.5 tracking-wide"
              style={{ color: HERITAGE_PALETTE.copper, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {yrs}
            </div>
          )}
          {birthPlace && (
            <div
              className="text-[9px] leading-tight truncate w-full flex items-center gap-0.5 justify-center"
              title={`Birthplace: ${birthPlace}${genLabel ? ` · ${genLabel}` : ""}`}
              style={{ color: HERITAGE_PALETTE.inkSoft }}
              data-testid={`tree-node-birthplace-${m.id}`}
            >
              {genLabel && (
                <span
                  className="mr-1 px-1 rounded-sm font-semibold tracking-wide"
                  style={{
                    fontSize: 8,
                    color: HERITAGE_PALETTE.copper,
                    background: "rgba(181,137,54,0.12)",
                    border: `1px solid ${HERITAGE_PALETTE.ringOuter}`,
                    lineHeight: "12px",
                  }}
                  data-testid={`tree-node-generation-${m.id}`}
                >
                  {genLabel}
                </span>
              )}
              {/* Birthplace uses a Home icon — this is the ancestral
                  home / origin place, not "current whereabouts". */}
              <Home className="w-2.5 h-2.5 flex-none opacity-60" aria-label="Birthplace" />
              <span className="truncate">{birthPlace}</span>
            </div>
          )}
          {currentLoc && currentLoc !== birthPlace && (
            <div
              className="text-[9px] leading-tight truncate w-full flex items-center gap-0.5 justify-center"
              title={`Current place: ${currentLoc}`}
              style={{ color: HERITAGE_PALETTE.inkSoft }}
              data-testid={`tree-node-currentloc-${m.id}`}
            >
              {/* Current-location uses a MapPin icon — matches the
                  intuition that a map-pin represents "where I am now". */}
              <MapPin className="w-2.5 h-2.5 flex-none opacity-60" aria-label="Current location" />
              <span className="truncate">{currentLoc}</span>
            </div>
          )}
          {!birthPlace && !currentLoc && genLabel && (
            <div className="text-[9px] leading-tight w-full flex items-center justify-center pb-1">
              <span
                className="px-1 rounded-sm font-semibold tracking-wide"
                style={{
                  fontSize: 8,
                  color: HERITAGE_PALETTE.copper,
                  background: "rgba(181,137,54,0.12)",
                  border: `1px solid ${HERITAGE_PALETTE.ringOuter}`,
                  lineHeight: "12px",
                }}
                data-testid={`tree-node-generation-${m.id}`}
              >
                {genLabel}
              </span>
            </div>
          )}
          {!birthPlace && !currentLoc && !genLabel && (
            <div className="pb-1" />
          )}
          {(birthPlace || currentLoc) && <div className="mt-auto" aria-hidden />}
        </div>

        {/* Copy-link icon — top-right, subtle */}
        <CopyLinkIcon
          url={m.memorial_slug ? `/celebrate/${m.memorial_slug}` : `/family/${treeSlug}/member/${m.id}`}
          position="tr"
          label={m.memorial_slug ? "Copy memorial link" : "Copy profile link"}
          testId={`tree-node-copy-${m.id}`}
        />
      </Link>
      {isAdmin && typeof onQuickEdit === "function" && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickEdit(); }}
          data-testid={`tree-node-quickedit-${m.id}`}
          aria-label="Quick edit"
          title="Quick edit"
          className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white/95 border shadow-sm flex items-center justify-center hover:scale-110 transition"
          style={{ borderColor: HERITAGE_PALETTE.border, zIndex: 5 }}
        >
          <Pencil className="w-3 h-3" style={{ color: HERITAGE_PALETTE.copper }} />
        </button>
      )}
    </div>
  );
}
